<template>
  <Modal v-model="visible" title="导入/导出" width="700px" :mask-closable="false" @on-ok="handleOk"
    @on-cancel="handleCancel">
    <div class="import-export-container">
      <!-- AI 生成话术 -->
      <div class="section" v-if="access.flowAiGenerate">
        <h3>AI 生成话术</h3>
        <p class="desc">上传 CSV 话术文档，调用 AI 生成仅包含 scenes 和 intents 的数据，并选择替换或追加。</p>
        <div class="upload-area">
          <Button type="success" ghost @click="openAiModal">
            <Icon type="md-flash" />
            打开 AI 生成
          </Button>
        </div>
      </div>

      <!-- AI 生成话术文档 -->
      <div class="section" v-if="access.flowAiGenerate">
        <h3>AI 生成话术文档</h3>
        <p class="desc">上传文档，调用 AI 生成话术文档表格（HTML 格式），并自动下载生成的文档。</p>
        <div class="upload-area">
          <Button type="success" ghost @click="openAiDocModal">
            <Icon type="md-document" />
            打开 AI 生成文档
          </Button>
        </div>
      </div>
      
      <!-- AI 知识库生成 -->
      <div class="section" v-if="access.flowAiGenerate">
        <h3>AI 知识库生成</h3>
        <p class="desc">上传文档，调用 AI 生成知识库 Excel（.xlsx），包含 类型/标题/关键词/机器人回答文本。</p>
        <div class="upload-area">
          <Button type="success" ghost @click="openAiIntentModal">
            <Icon type="md-construct" />
            打开 AI 知识库生成
          </Button>
        </div>
      </div>
      <!-- 导出文本 -->
      <div class="section">
        <h3>导出文本</h3>
        <p class="desc">导出场景和知识库中的文本内容，用于录音制作和文案修改</p>
        <Button type="primary" @click="exportText" :loading="exportLoading">
          <Icon type="md-download" />
          导出文本
        </Button>
      </div>

      <!-- 导入文本 -->
      <div class="section">
        <h3>导入文本</h3>
        <p class="desc">导入修改后的文本内容，批量更新场景和知识库</p>
        <div class="upload-area">
          <Upload ref="textUpload" :before-upload="beforeTextUpload" :format="['xlsx', 'xls']" :max-size="10240"
            accept=".xlsx,.xls" :show-upload-list="false">
            <Button type="primary" ghost>
              <Icon type="md-cloud-upload" />
              选择Excel文件
            </Button>
          </Upload>
          <p class="upload-tip">支持 .xlsx 和 .xls 格式，文件大小不超过10MB</p>
        </div>
      </div>

      <!-- 导入录音 -->
      <div class="section">
        <h3>导入录音</h3>
        <p class="desc">上传录音压缩包，自动匹配并更新对应的语音文件</p>
        <div class="upload-area">
          <Button type="primary" ghost @click="openAudioImport">
            <Icon type="md-cloud-upload" />
            选择ZIP压缩包
          </Button>
          <p class="upload-tip">支持 .zip 格式，文件大小不超过100MB</p>
        </div>
      </div>

      <!-- 导出JSON（可选范围） -->
      <div class="section">
        <h3>导出JSON</h3>
        <p class="desc">选择导出范围，下载包含所选内容的 JSON 文件</p>
        <div class="upload-area">
          <CheckboxGroup v-model="jsonExportSelection" style="margin-bottom: 12px; display: block;">
            <Checkbox label="scenes">场景</Checkbox>
            <Checkbox label="settings">流程设置</Checkbox>
            <Checkbox label="intents">意图管理</Checkbox>
            <Checkbox label="knowledge">问答知识库</Checkbox>
            <Checkbox label="rules">挂机处理</Checkbox>
            <Checkbox label="event_rules">事件</Checkbox>
          </CheckboxGroup>
          <Button type="primary" :loading="exportJsonLoading" @click="exportJson">
            <Icon type="md-download" />
            导出所选JSON
          </Button>
        </div>
      </div>

      <!-- 导入JSON -->
      <div class="section">
        <h3>导入JSON</h3>
        <p class="desc">导入完整话术 JSON 文件，可选择替换或增加（谨慎操作）</p>
        <div class="upload-area">
          <div style="margin-bottom: 12px;">
            <RadioGroup v-model="jsonImportMode" type="button">
              <Radio label="replace">替换</Radio>
              <Radio label="append">增加</Radio>
            </RadioGroup>
          </div>
          <Upload ref="jsonUpload" :before-upload="beforeJsonUpload" :format="['json']" :max-size="10240"
            accept=".json,application/json" :show-upload-list="false">
            <Button type="error" ghost>
              <Icon type="md-cloud-upload" />
              选择JSON文件
            </Button>
          </Upload>
          <p class="upload-tip">将以文件内容覆盖当前话术；建议先使用“导出文本/数据”备份</p>
        </div>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel">取消</Button>
    </div>

    <!-- 录音导入弹窗 -->
    <AudioImportModal v-model="audioImportVisible" />
    <!-- AI 生成弹窗 -->
    <AiGenerateModal v-model="aiModalVisible" />
    <!-- AI 生成话术文档弹窗 -->
    <AiGenerateDocModal v-model="aiDocModalVisible" />
    <!-- AI 知识库生成弹窗 -->
    <AiIntentGenerateModal v-model="aiIntentModalVisible" />
  </Modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as XLSX from 'xlsx'
import AudioImportModal from './AudioImportModal.vue'
import AiGenerateModal from './AiGenerateModal.vue'
import AiGenerateDocModal from './AiGenerateDocModal.vue'
import AiIntentGenerateModal from './AiIntentGenerateModal.vue'
import { addNodeId } from '@/utils/nodeIdPool'

export default {
  name: 'ImportExportModal',
  components: {
    AudioImportModal,
    AiGenerateModal,
    AiGenerateDocModal,
    AiIntentGenerateModal
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      exportLoading: false,
      textUpload: null,
      audioImportVisible: false,
      aiModalVisible: false,
      aiDocModalVisible: false,
      aiIntentModalVisible: false,
      exportJsonLoading: false,
      jsonExportSelection: [],
      jsonImportMode: 'replace'
    }
  },
  computed: {
    ...mapState('scene', ['scene', 'sceneId']),
    ...mapState('user', ['company']),
    ...mapState('user', ['access']),
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  methods: {
    ...mapActions('scene', ['saveScene']),

    // 导出文本
    async exportText() {
      this.exportLoading = true
      try {
        const exportData = this.generateExportData()
        this.downloadExcel(exportData)
        this.$Message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$Message.error('导出失败')
      } finally {
        this.exportLoading = false
      }
    },

    // 生成导出数据
    generateExportData() {
      const data = []
      // 导出场景文本
      if (this.scene.scenes) {
        this.scene.scenes.forEach(scene => {
          if (!scene.nodes) return
          scene.nodes.forEach(node => {
            if (!node.voices) return
            node.voices.forEach(voice => {
              if (voice.text) {
                data.push({
                  ID: `${this.sceneId}-scenes-${scene.id}-${node.id}-${voice.id}`,
                  Text: voice.text,
                })
              }
            })
          })
        })
      }
      // 导出知识库文本
      if (this.scene.knowledge) {
        this.scene.knowledge.forEach(knowledge => {
          if (!knowledge.responses) return
          knowledge.responses.forEach(response => {
            if (response.text) {
              data.push({
                ID: `${this.sceneId}-knowledge-${knowledge.id}-${response.id}`,
                Text: response.text,
              })
            }
          })
        })
      }

      // 超时播放语音
      if (this.scene.settings?.general?.timeout_voices?.[0]?.text) {
        data.push({
          ID: `${this.sceneId}-timeout-${this.scene.settings.general.timeout_voices[0].id}`,
          Text: this.scene.settings.general.timeout_voices[0].text,
        })
      }

      // 多开场⽩
      if (this.scene.settings) {
        const multi_prologue = this.scene.settings.multi_prologue || {}
        const voices = multi_prologue.voices || []
        voices.forEach(voice => {
          if (voice.text) {
            data.push({
              ID: `${this.sceneId}-multiPrologue-${voice.id}`,
              Text: voice.text,
            })
          }
        })
      }

      return data
    },

    // 下载Excel文件
    downloadExcel(data) {
      const worksheet = XLSX.utils.json_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '文本导出')

      // 设置列宽
      const colWidths = [
        { wch: 40 }, // ID
        { wch: 100 }, // Text
      ]
      worksheet['!cols'] = colWidths

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `文本导出_${this.sceneId}_${new Date().toISOString().slice(0, 10)}.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    },

    // 文本文件上传前处理
    beforeTextUpload(file) {
      this.importText(file)
      return false
    },

    // 导入文本
    async importText(file) {
      try {
        const data = await this.readExcelFile(file)
        await this.processImportedText(data)
        this.$Message.success('文本导入成功')
      } catch (error) {
        console.error('文本导入失败:', error)
        this.$Message.error('文本导入失败')
      }
    },

    // 读取Excel文件
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const worksheet = workbook.Sheets[workbook.SheetNames[0]]
            const jsonData = XLSX.utils.sheet_to_json(worksheet)
            resolve(jsonData)
          } catch (error) {
            reject(error)
          }
        }
        reader.onerror = reject
        reader.readAsArrayBuffer(file)
      })
    },

    // 处理导入的文本
    async processImportedText(data) {
      const newScene = JSON.parse(JSON.stringify(this.scene))
      let hasChanges = false
      data.forEach(row => {
        if (!row.ID || !row.Text) return;
        const parts = row.ID.split('-')
        if (parts.length < 3) return;
        const sceneId = parts[0]
        if (sceneId !== `${this.sceneId}`) return;
        const type = parts[1]
        if (type === 'scenes') {
          const [, , sceneId, nodeId, voiceId] = parts
          const scene = newScene.scenes.find(scene => scene.id === sceneId)
          if (!scene) return;
          const node = scene.nodes.find(node => node.id === nodeId)
          if (!node) return;
          const voice = node.voices.find(voice => voice.id === voiceId)
          if (!voice) return;
          voice.text = row.Text
          hasChanges = true
        } else if (type === 'knowledge') {
          const [, , knowledgeId, responseId] = parts
          const knowledge = newScene.knowledge.find(knowledge => knowledge.id === knowledgeId)
          if (!knowledge) return;
          const response = knowledge.responses.find(response => response.id === responseId)
          if (!response) return;
          response.text = row.Text
          hasChanges = true
        } else if (type === 'timeout') {
          const [, , voiceId] = parts
          if (!newScene.settings.general.timeout_voices) return;
          const timeoutVoice = newScene.settings.general.timeout_voices.find(voice => voice.id === voiceId)
          if (!timeoutVoice) return;
          timeoutVoice.text = row.Text
          hasChanges = true
        } else if (type === 'multiPrologue') {
          const [, , voiceId] = parts
          if (!newScene.settings.multi_prologue.voices) return;
          const multiPrologueVoice = newScene.settings.multi_prologue.voices.find(voice => voice.id === voiceId)
          if (!multiPrologueVoice) return;
          multiPrologueVoice.text = row.Text
          hasChanges = true
        }
      })
      if (hasChanges) {
        try {
          await this.saveScene(newScene)
        } catch (error) {
          console.error('保存场景失败:', error)
          throw new Error('保存场景失败')
        }
      }
    },

    // 打开录音导入弹窗
    openAudioImport() {
      this.audioImportVisible = true
    },

    // 打开 AI 生成弹窗
    openAiModal() {
      this.aiModalVisible = true
    },

    // 打开 AI 生成文档弹窗
    openAiDocModal() {
      this.aiDocModalVisible = true
    },

    // 打开 AI 知识库生成弹窗
    openAiIntentModal() {
      this.aiIntentModalVisible = true
    },

    // JSON 文件上传前处理
    beforeJsonUpload(file) {
      this.importJson(file)
      return false
    },

    // 读取并导入 JSON
    async importJson(file) {
      try {
        const json = await this.readJsonFile(file)
        // 可选：确保基本结构存在
        const newScene = json && typeof json === 'object' ? json : {}
        if (this.jsonImportMode === 'replace') {
          await this.saveScene({
            ...this.scene,
            ...newScene,
            id: this.scene.id,
          })
        } else {
          const merged = this.mergeSceneAppend(this.scene, newScene)
          await this.saveScene(merged)
        }
        this.$Message.success('JSON 导入成功')
      } catch (error) {
        console.error('JSON 导入失败:', error)
        this.$Message.error('JSON 导入失败，请检查文件内容格式是否正确')
      }
    },

    // 读取 JSON 文件
    readJsonFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const text = e.target.result
            const json = JSON.parse(text)
            resolve(json)
          } catch (err) {
            reject(err)
          }
        }
        reader.onerror = reject
        reader.readAsText(file, 'utf-8')
      })
    },

    // 追加合并：数组按 id 去重，对象浅合并
    mergeSceneAppend(base, incoming) {
      const result = { ...base }
      const arrayKeys = ['scenes', 'intents', 'knowledge']
      const noIdArrayKeys = ['rules', 'event_rules']
      const objectKeys = ['settings']
      const nodeIdPool = { scenes: 'F', intents: 'I', knowledge: 'K' }

      arrayKeys.forEach(key => {
        const baseArr = Array.isArray(base[key]) ? base[key] : []
        const incArr = Array.isArray(incoming[key]) ? incoming[key] : []
        if (!incArr.length) return

        incArr.forEach(item => {
          baseArr.push({
            ...item,
            id: addNodeId(nodeIdPool[key])
          })
        })
        result[key] = baseArr
      })

      noIdArrayKeys.forEach(key => {
        const baseArr = Array.isArray(base[key]) ? base[key] : []
        const incArr = Array.isArray(incoming[key]) ? incoming[key] : []
        if (!incArr.length) return
        incArr.forEach(item => {
          baseArr.push(item)
        })
        result[key] = baseArr
      })

      objectKeys.forEach(key => {
        if (incoming[key] && typeof incoming[key] === 'object') {
          result[key] = {
            ...(base[key] || {}),
            ...incoming[key]
          }
        }
      })

      // 其他顶层简单字段保留原 scene（避免误覆盖），仅在 replace 模式覆盖
      result.id = base.id
      return result
    },

    // 导出所选 JSON 范围
    exportJson() {
      if (!this.jsonExportSelection.length) {
        this.$Message.warning('请先选择导出范围')
        return
      }
      this.exportJsonLoading = true
      try {
        const exportObj = {}
        const selectableKeys = ['scenes', 'settings', 'intents', 'knowledge', 'rules', 'event_rules']
        this.jsonExportSelection.forEach(key => {
          if (selectableKeys.includes(key)) {
            exportObj[key] = this.scene[key]
          }
        })
        const content = JSON.stringify(exportObj, null, 2)
        const blob = new Blob([content], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const picked = this.jsonExportSelection.join('+')
        const today = new Date().toISOString().slice(0, 10)
        link.href = url
        link.download = `scene_${this.sceneId}_${picked}_${today}.json`
        link.click()
        URL.revokeObjectURL(url)
        this.$Message.success('导出成功')
      } catch (error) {
        console.error('导出JSON失败:', error)
        this.$Message.error('导出失败')
      } finally {
        this.exportJsonLoading = false
      }
    },

    handleOk() {
      this.visible = false
    },

    handleCancel() {
      this.visible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.import-export-container {
  .section {
    margin-bottom: 24px;
    padding: 16px;
    border: 1px solid #e8eaec;
    border-radius: 6px;
    background-color: #fafafa;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #17233d;
    }

    .desc {
      margin: 0 0 16px 0;
      color: #808695;
      font-size: 14px;
      line-height: 1.5;
    }

    .upload-area {
      .upload-tip {
        margin: 8px 0 0 0;
        color: #c5c8ce;
        font-size: 12px;
      }
    }
  }
}
</style>
